import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, Eye, EyeOff, Copy, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function RegenCodesModal({ open, onClose, onComplete }) {
  const [step, setStep] = useState("auth"); // "auth" | "codes"
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [codes, setCodes] = useState([]);
  const [confirmed, setConfirmed] = useState(false);

  // Reset all state every time the modal opens — guarantees fresh password entry
  useEffect(() => {
    if (open) {
      setStep("auth");
      setPassword("");
      setShowPassword(false);
      setLoading(false);
      setCodes([]);
      setConfirmed(false);
    }
  }, [open]);

  const handleRegenerate = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { data } = await api.post("/pro/regenerate-codes", { password });
      setCodes(data.recovery_codes);
      setStep("codes");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to regenerate codes";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(codes.join("\n"));
    toast.success("Recovery codes copied");
  };

  const handleDownload = () => {
    const text = [
      "QuickInbox PRO — Recovery Codes (Regenerated)",
      `Generated: ${new Date().toISOString()}`,
      "",
      "Each code can only be used ONCE.",
      "Store these codes in a safe place.",
      "",
      ...codes.map((c, i) => `${i + 1}. ${c}`),
    ].join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quickinbox-recovery-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Recovery codes downloaded");
  };

  const handleFinish = () => {
    if (!confirmed) {
      toast.error("Please confirm you've saved your recovery codes");
      return;
    }
    setPassword("");
    setStep("auth");
    setCodes([]);
    setConfirmed(false);
    onComplete?.();
  };

  const handleClose = () => {
    if (step === "codes") return; // can't close while viewing codes
    setPassword("");
    setStep("auth");
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-[460px] p-0 [&>button]:hidden"
        onPointerDownOutside={(e) => { if (step === "codes") e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (step === "codes") e.preventDefault(); }}
        data-testid="regen-codes-modal"
      >
        <div className="max-h-[85vh] overflow-y-auto p-6">
        {step === "auth" ? (
          <>
            <DialogHeader>
              <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center mb-2">
                <RefreshCw size={20} className="text-white" />
              </div>
              <DialogTitle className="text-lg font-semibold">Regenerate recovery codes</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                This will replace all existing recovery codes with a new set. Old codes will stop working immediately. Enter your password to continue.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleRegenerate} className="space-y-4 pt-4">
              <div className="space-y-1.5">
                <Label htmlFor="regen-password" className="text-xs text-zinc-500">Current password</Label>
                <div className="relative">
                  <Input
                    id="regen-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="pr-10"
                    autoFocus
                    data-testid="regen-codes-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#888888]"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1 h-10"
                  data-testid="regen-codes-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !password}
                  className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white h-10"
                  data-testid="regen-codes-submit"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : "Regenerate"}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">New recovery codes</DialogTitle>
              <DialogDescription className="text-sm text-zinc-500">
                Your old codes have been invalidated. Save these new codes — they are shown <strong className="text-[#555555]">only once</strong>.
              </DialogDescription>
            </DialogHeader>

            <div className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-4 my-3" data-testid="regen-codes-list">
              <div className="grid grid-cols-2 gap-2">
                {codes.map((code, i) => (
                  <div key={i} className="font-mono text-sm text-[#555555] bg-white rounded px-2.5 py-1.5 border border-[#E5E5E5] text-center tracking-wider">
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={handleCopy} data-testid="regen-codes-copy">
                <Copy size={13} className="mr-1.5" /> Copy codes
              </Button>
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={handleDownload} data-testid="regen-codes-download">
                <Download size={13} className="mr-1.5" /> Download
              </Button>
            </div>

            <div className="flex items-start gap-2.5 mt-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <Checkbox
                id="regen-confirmed"
                checked={confirmed}
                onCheckedChange={setConfirmed}
                data-testid="regen-codes-confirm-checkbox"
              />
              <label htmlFor="regen-confirmed" className="text-xs text-amber-800 leading-relaxed cursor-pointer">
                I have saved my new recovery codes in a secure location. I understand that the old codes no longer work.
              </label>
            </div>

            <Button
              onClick={handleFinish}
              disabled={!confirmed}
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white h-10 mt-2"
              data-testid="regen-codes-finish"
            >
              Done
            </Button>
          </>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
